import { useState } from 'react';
import { api, ApiError, formatDate, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import {
  ACCREDITATION_INSTITUTION_TYPES,
  ACCREDITATION_STATES,
  PROGRAMME_LEVELS,
  type AccreditationInstitutionType,
  type AccreditedProgramme,
  type InstitutionAccreditation,
  type Page,
  type ProgrammeLevel,
} from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, Spinner, StatusBadge, Tabs } from '../../ui/components';

/**
 * Tertiary/TVET institution accreditation
 * (packages/modules/m29-tertiary-tvet/src/routes/accreditation.ts): the
 * review-fee-gated pipeline through a site visit to a decision, ongoing
 * programme management once accredited, and the periodic re-review cycle.
 */
export function Accreditation() {
  const { persona } = useAuth();
  const isPanel = persona?.roles.includes('ACCREDITATION_PANEL') ?? false;
  const [tab, setTab] = useState<'applications' | 'due'>('applications');

  return (
    <div>
      <PageHeader title="Institution Accreditation" subtitle="University, TVET college and private-tertiary accreditation review." />
      <Tabs tabs={[{ key: 'applications', label: 'Applications' }, { key: 'due', label: 'Due for Review' }]} value={tab} onChange={setTab} />
      {!isPanel ? <ErrorNote message="Listing and deciding accreditation applications requires the Accreditation Panel role." /> : null}
      {tab === 'applications' ? <ApplicationsTab isPanel={isPanel} /> : <DueForReviewTab isPanel={isPanel} />}
    </div>
  );
}

function ApplicationsTab({ isPanel }: { isPanel: boolean }) {
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);

  const qs = new URLSearchParams({ limit: '50' });
  if (status) qs.set('status', status);
  if (q) qs.set('q', q);
  const listQ = useApiQuery<Page<InstitutionAccreditation>>(isPanel ? `/api/v1/m29/accreditation/applications?${qs.toString()}` : null);

  if (!isPanel) return null;

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-2">
          <input className="field-input !w-52" placeholder="Search institution name…" value={q} onChange={(e) => setQ(e.target.value)} />
          <select className="field-input !w-48" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All states</option>
            {ACCREDITATION_STATES.map((s) => (
              <option key={s} value={s}>
                {s.replaceAll('_', ' ')}
              </option>
            ))}
          </select>
        </div>
        <button className="btn-primary" onClick={() => setShowCreate(true)}>
          + Submit application (assisted intake)
        </button>
      </div>

      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No accreditation applications match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Institution</th>
                <th className="table-head">Type</th>
                <th className="table-head text-right">Programmes</th>
                <th className="table-head">Accreditation no.</th>
                <th className="table-head">State</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((a) => (
                <tr key={a.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setDetailId(a.id)}>
                  <td className="table-cell font-medium">{a.institutionName}</td>
                  <td className="table-cell">
                    <StatusBadge status={a.institutionType} />
                  </td>
                  <td className="table-cell text-right tabular-nums">{a.programmes.length}</td>
                  <td className="table-cell font-mono text-xs">{a.accreditationNo ?? '—'}</td>
                  <td className="table-cell">
                    <StatusBadge status={a.state} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showCreate ? (
        <CreateApplicationModal
          onClose={() => setShowCreate(false)}
          onDone={() => {
            setShowCreate(false);
            listQ.reload();
          }}
        />
      ) : null}
      {detailId ? <ApplicationDetailModal id={detailId} onClose={() => setDetailId(null)} onChanged={() => listQ.reload()} /> : null}
    </div>
  );
}

function DueForReviewTab({ isPanel }: { isPanel: boolean }) {
  const [detailId, setDetailId] = useState<string | null>(null);
  const dueQ = useApiQuery<{ items: InstitutionAccreditation[] }>(isPanel ? '/api/v1/m29/accreditation/due-for-review' : null);
  if (!isPanel) return null;
  return (
    <div>
      {dueQ.error ? <ErrorNote message={dueQ.error} /> : null}
      <div className="card overflow-x-auto">
        {dueQ.loading ? (
          <Spinner />
        ) : (dueQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="Nothing is due for periodic re-review" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Institution</th>
                <th className="table-head">Accreditation no.</th>
                <th className="table-head">Next review due</th>
              </tr>
            </thead>
            <tbody>
              {dueQ.data!.items.map((a) => (
                <tr key={a.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setDetailId(a.id)}>
                  <td className="table-cell font-medium">{a.institutionName}</td>
                  <td className="table-cell font-mono text-xs">{a.accreditationNo}</td>
                  <td className="table-cell text-rose-600">{formatDate(a.nextReviewDueAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {detailId ? <ApplicationDetailModal id={detailId} onClose={() => setDetailId(null)} onChanged={() => dueQ.reload()} /> : null}
    </div>
  );
}

function CreateApplicationModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [institutionName, setInstitutionName] = useState('');
  const [institutionType, setInstitutionType] = useState<AccreditationInstitutionType>('UNIVERSITY');
  const [programmes, setProgrammes] = useState<Array<{ programmeName: string; level: ProgrammeLevel; nqfLevel: string }>>([
    { programmeName: '', level: 'BACHELOR', nqfLevel: '' },
  ]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m29/accreditation/applications', {
        institutionName,
        institutionType,
        programmes: programmes
          .filter((p) => p.programmeName.trim().length > 0)
          .map((p) => ({ programmeName: p.programmeName, level: p.level, nqfLevel: p.nqfLevel ? Number.parseInt(p.nqfLevel, 10) : undefined })),
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to submit application');
    } finally {
      setBusy(false);
    }
  };

  const validProgrammes = programmes.some((p) => p.programmeName.trim().length >= 2);

  return (
    <Modal title="Submit accreditation application" onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <p className="text-xs text-slate-400">
          Submits against your own signed-in identity as the applicant (X-Acting-For institution mandates are not wired up in this dev
          build). Creates a real SeyPay review-fee intent.
        </p>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Institution name">
            <input className="field-input" value={institutionName} onChange={(e) => setInstitutionName(e.target.value)} />
          </Field>
          <Field label="Institution type">
            <select className="field-input" value={institutionType} onChange={(e) => setInstitutionType(e.target.value as AccreditationInstitutionType)}>
              {ACCREDITATION_INSTITUTION_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t.replaceAll('_', ' ')}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <Field label="Programmes">
          <div className="space-y-2">
            {programmes.map((p, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <input
                  className="field-input flex-1"
                  placeholder="Programme name"
                  value={p.programmeName}
                  onChange={(e) => setProgrammes((ps) => ps.map((pp, i) => (i === idx ? { ...pp, programmeName: e.target.value } : pp)))}
                />
                <select
                  className="field-input !w-40"
                  value={p.level}
                  onChange={(e) => setProgrammes((ps) => ps.map((pp, i) => (i === idx ? { ...pp, level: e.target.value as ProgrammeLevel } : pp)))}
                >
                  {PROGRAMME_LEVELS.map((l) => (
                    <option key={l} value={l}>
                      {l.replaceAll('_', ' ')}
                    </option>
                  ))}
                </select>
                <input
                  className="field-input !w-24"
                  type="number"
                  min="1"
                  max="10"
                  placeholder="NQF"
                  value={p.nqfLevel}
                  onChange={(e) => setProgrammes((ps) => ps.map((pp, i) => (i === idx ? { ...pp, nqfLevel: e.target.value } : pp)))}
                />
                {programmes.length > 1 ? (
                  <button className="text-xs text-rose-500" onClick={() => setProgrammes((ps) => ps.filter((_, i) => i !== idx))}>
                    Remove
                  </button>
                ) : null}
              </div>
            ))}
            <button
              className="btn-secondary !px-2.5 !py-1 text-xs"
              onClick={() => setProgrammes((ps) => [...ps, { programmeName: '', level: 'BACHELOR', nqfLevel: '' }])}
            >
              + Add programme
            </button>
          </div>
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || institutionName.trim().length < 2 || !validProgrammes}>
            {busy ? 'Submitting…' : 'Submit application'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function ApplicationDetailModal({ id, onClose, onChanged }: { id: string; onClose: () => void; onChanged: () => void }) {
  const detailQ = useApiQuery<InstitutionAccreditation>(`/api/v1/m29/accreditation/applications/${id}`);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [scheduledAt, setScheduledAt] = useState('');
  const [showSchedule, setShowSchedule] = useState(false);
  const [visitNotes, setVisitNotes] = useState('');
  const [showComplete, setShowComplete] = useState(false);
  const [showDecision, setShowDecision] = useState<'approve' | 'reject' | null>(null);
  const [decisionNotes, setDecisionNotes] = useState('');
  const [reviewCycleYears, setReviewCycleYears] = useState('5');
  const [showReasonFor, setShowReasonFor] = useState<'suspend' | 'revoke' | null>(null);
  const [reason, setReason] = useState('');
  const [showAddProgramme, setShowAddProgramme] = useState(false);
  const [newProgrammeName, setNewProgrammeName] = useState('');
  const [newProgrammeLevel, setNewProgrammeLevel] = useState<ProgrammeLevel>('CERTIFICATE');

  const app = detailQ.data;

  const act = async (path: string, body?: unknown) => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m29/accreditation/applications/${id}${path}`, body);
      detailQ.reload();
      onChanged();
      setShowSchedule(false);
      setShowComplete(false);
      setShowDecision(null);
      setShowReasonFor(null);
      setShowAddProgramme(false);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  const programmeAct = async (programmeId: string, path: string) => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m29/accreditation/applications/${id}/programmes/${programmeId}${path}`);
      detailQ.reload();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Accreditation application" onClose={onClose} wide>
      {detailQ.loading || !app ? (
        <Spinner />
      ) : (
        <div className="space-y-4 text-sm">
          {error ? <ErrorNote message={error} /> : null}
          <div className="flex items-center gap-2">
            <StatusBadge status={app.state} />
            <span className="font-semibold">{app.institutionName}</span>
            <StatusBadge status={app.institutionType} />
          </div>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-1.5">
            <div className="flex justify-between">
              <dt className="text-slate-500">Fee</dt>
              <dd>
                {scr(app.feeAmount)} <StatusBadge status={app.feePaid ? 'PAID' : 'PENDING'} />
              </dd>
            </div>
            {app.accreditationNo ? (
              <div className="flex justify-between">
                <dt className="text-slate-500">Accreditation no.</dt>
                <dd className="font-mono text-xs">{app.accreditationNo}</dd>
              </div>
            ) : null}
            {app.nextReviewDueAt ? (
              <div className="flex justify-between">
                <dt className="text-slate-500">Next review due</dt>
                <dd className="font-medium">{formatDate(app.nextReviewDueAt)}</dd>
              </div>
            ) : null}
          </dl>
          {app.decisionNotes ? <p className="text-xs text-slate-500">Decision notes: {app.decisionNotes}</p> : null}

          <div className="border-t border-slate-100 pt-3">
            <div className="mb-1.5 flex items-center justify-between">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Programmes</div>
              {app.state === 'ACCREDITED' ? (
                <button className="btn-secondary !px-2.5 !py-1 text-xs" onClick={() => setShowAddProgramme((s) => !s)}>
                  + Add programme
                </button>
              ) : null}
            </div>
            {showAddProgramme ? (
              <div className="mb-2 flex items-end gap-2 rounded-lg border border-slate-200 p-2.5">
                <Field label="Programme name" className="!mb-0 flex-1">
                  <input className="field-input !py-1 text-xs" value={newProgrammeName} onChange={(e) => setNewProgrammeName(e.target.value)} />
                </Field>
                <Field label="Level" className="!mb-0">
                  <select className="field-input !py-1 text-xs" value={newProgrammeLevel} onChange={(e) => setNewProgrammeLevel(e.target.value as ProgrammeLevel)}>
                    {PROGRAMME_LEVELS.map((l) => (
                      <option key={l} value={l}>
                        {l.replaceAll('_', ' ')}
                      </option>
                    ))}
                  </select>
                </Field>
                <button
                  className="btn-primary !px-2.5 !py-1 text-xs"
                  disabled={busy || newProgrammeName.trim().length < 2}
                  onClick={() => act('/programmes', { programmeName: newProgrammeName, level: newProgrammeLevel })}
                >
                  Add
                </button>
              </div>
            ) : null}
            {app.programmes.length === 0 ? (
              <p className="text-xs text-slate-400">No programmes listed.</p>
            ) : (
              <ul className="space-y-1.5">
                {app.programmes.map((p: AccreditedProgramme) => (
                  <li key={p.id} className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-1.5 text-xs">
                    <span>
                      {p.programmeName} — {p.level.replaceAll('_', ' ')}
                      {p.nqfLevel ? ` (NQF ${p.nqfLevel})` : ''}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <StatusBadge status={p.status} />
                      {p.status === 'PENDING' ? (
                        <button className="btn-secondary !px-2 !py-0.5 text-[11px]" disabled={busy} onClick={() => programmeAct(p.id, '/approve')}>
                          Approve
                        </button>
                      ) : null}
                      {p.status === 'ACTIVE' ? (
                        <button className="btn-danger !px-2 !py-0.5 text-[11px]" disabled={busy} onClick={() => programmeAct(p.id, '/withdraw')}>
                          Withdraw
                        </button>
                      ) : null}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="flex flex-wrap gap-1.5 border-t border-slate-100 pt-3">
            {app.state === 'SUBMITTED' ? (
              <button
                className="btn-primary !px-2.5 !py-1 text-xs"
                disabled={busy || !app.feePaid}
                title={!app.feePaid ? 'Review fee not confirmed yet' : undefined}
                onClick={() => act('/review')}
              >
                Begin review
              </button>
            ) : null}
            {app.state === 'UNDER_REVIEW' ? (
              showSchedule ? (
                <div className="flex items-end gap-2">
                  <Field label="Site visit date/time" className="!mb-0">
                    <input type="datetime-local" className="field-input !py-1 text-xs" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} />
                  </Field>
                  <button
                    className="btn-primary !px-2.5 !py-1 text-xs"
                    disabled={busy || !scheduledAt}
                    onClick={() => act('/schedule-site-visit', { scheduledAt: new Date(scheduledAt).toISOString() })}
                  >
                    Schedule
                  </button>
                </div>
              ) : (
                <button className="btn-primary !px-2.5 !py-1 text-xs" onClick={() => setShowSchedule(true)}>
                  Schedule site visit
                </button>
              )
            ) : null}
            {app.state === 'SITE_VISIT_SCHEDULED' ? (
              showComplete ? (
                <div className="flex items-end gap-2">
                  <Field label="Site visit notes" className="!mb-0 flex-1">
                    <input className="field-input !py-1 text-xs" value={visitNotes} onChange={(e) => setVisitNotes(e.target.value)} />
                  </Field>
                  <button
                    className="btn-primary !px-2.5 !py-1 text-xs"
                    disabled={busy || visitNotes.trim().length < 3}
                    onClick={() => act('/site-visit/complete', { notes: visitNotes })}
                  >
                    Complete visit
                  </button>
                </div>
              ) : (
                <button className="btn-primary !px-2.5 !py-1 text-xs" onClick={() => setShowComplete(true)}>
                  Record site visit complete
                </button>
              )
            ) : null}
            {app.state === 'SITE_VISIT_COMPLETED' && showDecision === null ? (
              <>
                <button className="btn-success !px-2.5 !py-1 text-xs" onClick={() => setShowDecision('approve')}>
                  Accredit
                </button>
                <button className="btn-danger !px-2.5 !py-1 text-xs" onClick={() => setShowDecision('reject')}>
                  Reject
                </button>
              </>
            ) : null}
            {app.state === 'ACCREDITED' ? (
              <>
                <button className="btn-danger !px-2.5 !py-1 text-xs" onClick={() => setShowReasonFor('suspend')}>
                  Suspend
                </button>
                <button className="btn-secondary !px-2.5 !py-1 text-xs" disabled={busy} onClick={() => act('/review-cycle/start')}>
                  Start periodic review
                </button>
              </>
            ) : null}
            {app.state === 'SUSPENDED' ? (
              <button className="btn-success !px-2.5 !py-1 text-xs" disabled={busy} onClick={() => act('/reinstate')}>
                Reinstate
              </button>
            ) : null}
            {['ACCREDITED', 'SUSPENDED'].includes(app.state) ? (
              <button className="btn-danger !px-2.5 !py-1 text-xs" onClick={() => setShowReasonFor('revoke')}>
                Revoke
              </button>
            ) : null}
          </div>

          {showDecision ? (
            <div className="space-y-2 rounded-lg border border-slate-200 p-3">
              <Field label="Decision notes">
                <textarea className="field-input" rows={2} value={decisionNotes} onChange={(e) => setDecisionNotes(e.target.value)} />
              </Field>
              {showDecision === 'approve' ? (
                <Field label="Review cycle (years)">
                  <input className="field-input !w-32" type="number" min="1" value={reviewCycleYears} onChange={(e) => setReviewCycleYears(e.target.value)} />
                </Field>
              ) : null}
              <div className="flex justify-end gap-2">
                <button className="btn-secondary !px-2.5 !py-1 text-xs" onClick={() => setShowDecision(null)}>
                  Cancel
                </button>
                <button
                  className={showDecision === 'approve' ? 'btn-success !px-2.5 !py-1 text-xs' : 'btn-danger !px-2.5 !py-1 text-xs'}
                  disabled={busy || decisionNotes.trim().length < 3}
                  onClick={() =>
                    act('/decision', {
                      approve: showDecision === 'approve',
                      notes: decisionNotes,
                      ...(showDecision === 'approve' ? { reviewCycleYears: Number.parseInt(reviewCycleYears, 10) } : {}),
                    })
                  }
                >
                  Confirm {showDecision === 'approve' ? 'accreditation' : 'rejection'}
                </button>
              </div>
            </div>
          ) : null}

          {showReasonFor ? (
            <div className="flex items-end gap-2 rounded-lg border border-slate-200 p-3">
              <Field label="Reason" className="!mb-0 flex-1">
                <input className="field-input" value={reason} onChange={(e) => setReason(e.target.value)} />
              </Field>
              <button className="btn-danger" disabled={busy || reason.trim().length < 3} onClick={() => act(`/${showReasonFor}`, { reason })}>
                Confirm
              </button>
              <button className="btn-secondary" onClick={() => setShowReasonFor(null)}>
                Cancel
              </button>
            </div>
          ) : null}
        </div>
      )}
    </Modal>
  );
}
